import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import SortableHeader from '../../components/common/SortableHeader';
import * as XLSX from 'xlsx';

const Dashboard = () => {
  const { showToast } = useUI();
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    kpis: { totalStockValue: 0, totalItems: 0, lowStockAlerts: 0, pendingOrders: 0 },
    activityChart: [],
    lowStockProducts: [],
    recentActivities: [],
    stockOutRisks: []
  });
  const [activitySortBy, setActivitySortBy] = useState('createdAt');
  const [activitySortOrder, setActivitySortOrder] = useState('DESC');

  const { user, loading: loadingAuth } = useAuth();
  const { socket } = useSocket();

  const getPoReference = (desc) => {
    if (!desc) return null;
    const match = desc.match(/commande achat ([a-zA-Z0-9-]+)/i);
    return match ? match[1] : null;
  };

  const fetchStats = async () => {
    if (!user?.companyId) return;
    try {
      const params = new URLSearchParams({
        companyId: user.companyId,
        activitySortBy,
        activitySortOrder
      });
      const res = await fetch(`/api/dashboard/stats?${params.toString()}`);
      if (res.ok) {
        const dashboardData = await res.json();

        // Fetch stock out risks separately
        try {
          const risksRes = await fetch(`/api/analytics/stock-out-risks?companyId=${user.companyId}&limit=5`);
          if (risksRes.ok) {
            dashboardData.stockOutRisks = await risksRes.json();
          }
        } catch (e) {
          console.error("Failed to fetch stock out risks", e);
        }

        setStats(dashboardData);
      }
    } catch (error) {
      showToast("Erreur lors du chargement des statistiques", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loadingAuth && user?.companyId) {
      fetchStats();
    } else if (!loadingAuth && !user?.companyId) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAuth, user?.companyId, activitySortBy, activitySortOrder]);

  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      if (!loadingAuth && user?.companyId) {
        fetchStats();
      }
    };

    socket.on('stock_updated', handleUpdate);
    socket.on('product_updated', handleUpdate);

    return () => {
      socket.off('stock_updated', handleUpdate);
      socket.off('product_updated', handleUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, loadingAuth, user?.companyId]);

  // eslint-disable-next-line no-unused-vars
  const handleExport = () => {
    try {
      showToast("Préparation de l'exportation du tableau de bord...", "info");

      // 1. KPIs
      const kpiData = [
        { 'Indicateur': 'Valeur Totale Stock', 'Valeur': `${Number(stats.kpis.totalStockValue).toLocaleString()} €` },
        { 'Indicateur': 'Articles en Stock', 'Valeur': stats.kpis.totalItems },
        { 'Indicateur': 'Alertes Stock Bas', 'Valeur': stats.kpis.lowStockAlerts },
        { 'Indicateur': 'Commandes en cours', 'Valeur': stats.kpis.pendingOrders }
      ];

      // 2. Activités Récentes
      const activityData = stats.recentActivities.map(act => ({
        'Article': act.product?.name || 'Inconnu',
        'Type': act.type,
        'Quantité': act.quantity,
        'Date': new Date(act.createdAt).toLocaleString('fr-FR'),
        'Statut': 'Complété'
      }));

      const wb = XLSX.utils.book_new();

      const wsKPIs = XLSX.utils.json_to_sheet(kpiData);
      XLSX.utils.book_append_sheet(wb, wsKPIs, 'Indicateurs');

      const wsActivity = XLSX.utils.json_to_sheet(activityData);
      XLSX.utils.book_append_sheet(wb, wsActivity, 'Activité Récente');

      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Dashboard_Export_${date}.xlsx`);

      showToast("Export terminé !", "success");
    } catch (error) {
      console.error('Export error:', error);
      showToast("Erreur lors de l'exportation", "error");
    }
  };

  if (loading) return <div className="p-10 text-center animate-pulse text-slate-500">Chargement de votre centre de pilotage...</div>;

  const activityData = stats?.activityChart || [];
  const maxActivity = Math.max(...activityData.map(d => d.value), 1);

  return (
    <>
      {/* Header Section - Sticky */}
      <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
        <div>
          <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Tableau de Bord</h1>
          <p className="text-slate-500 dark:text-slate-400">Aperçu en temps réel de votre inventaire et flux de stock.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* 
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            <span className="material-symbols-outlined text-lg">download</span>
            Exporter
          </button> 
          */}
          {/* <button
            onClick={() => setShowNewItemModal(true)}
            className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            <span>Nouvel Article</span>
          </button> */}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <KPICard title="Valeur Totale" value={`${Number(stats.kpis.totalStockValue).toLocaleString()} €`} trend="+0%" trendType="neutral" icon="payments" path="/analytics" />
        <KPICard title="Articles en Stock" value={Number(stats.kpis.totalItems).toLocaleString()} trend="+0%" trendType="neutral" icon="inventory" path="/inventory" />
        <KPICard title="Alertes Stock Bas" value={stats.kpis.lowStockAlerts} trend="Action requise" trendType="neutral" icon="warning" isWarning path="/smart-order" />
        <KPICard title="Commandes en cours" value={stats.kpis.pendingOrders} trend="En attente" trendType="neutral" icon="shopping_cart" path="/purchase-orders" />
      </div>

      {/* Activity & Lists Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Chart (Now clickable) */}
        <Link to="/analytics" className="lg:col-span-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-all group shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold dark:text-white group-hover:text-primary transition-colors">Analyse de l'Activité</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase">Voir les détails</span>
              <span className="material-symbols-outlined text-slate-400 text-sm">arrow_forward</span>
            </div>
          </div>
          <div className="h-64 flex items-end gap-3 px-2">
            {activityData.length > 0 ? activityData.map((day, idx) => (
              <Bar
                key={idx}
                height={`${(day.value / maxActivity) * 90}%`}
                label={day.label}
                active={day.isToday}
              />
            )) : (
              <div className="flex-1 h-full flex items-center justify-center text-slate-300 text-xs italic">
                Aucune activité enregistrée
              </div>
            )}
          </div>
          <div className="flex justify-between mt-4 text-[10px] font-bold text-slate-400 px-2 uppercase tracking-tight">
            {activityData.map((day, idx) => <span key={idx} className={day.isToday ? 'text-primary' : ''}>{day.label}</span>)}
          </div>
        </Link>

        {/* Stock Out Risks Section */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
              <span className="material-symbols-outlined text-rose-500">timer</span>
              Risques de Rupture de Stock
            </h3>
            <span className="text-xs font-bold text-slate-400 uppercase">Basé sur les 30 derniers jours</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {stats.stockOutRisks && stats.stockOutRisks.length > 0 ? stats.stockOutRisks.map(product => (
              <PredictionCard key={product.id} product={product} />
            )) : (
              <div className="col-span-full py-8 text-center bg-slate-50 dark:bg-slate-800/20 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 text-slate-400 text-sm italic">
                Aucun risque de rupture imminent détecté
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <h3 className="text-lg font-bold dark:text-white">Activité Récente</h3>
          <Link
            to="/analytics"
            className="text-primary text-sm font-bold hover:underline flex items-center gap-1"
          >
            Voir tout
            <span className="material-symbols-outlined text-sm">open_in_new</span>
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-400 text-[10px] uppercase font-bold tracking-widest">
              <tr>
                <SortableHeader
                  label="Produit"
                  field="product.name"
                  currentSort={activitySortBy}
                  currentOrder={activitySortOrder}
                  onSort={(f, o) => { setActivitySortBy(f); setActivitySortOrder(o); }}
                  className="px-6 py-4 w-[35%]"
                />
                <SortableHeader
                  label="Type"
                  field="type"
                  currentSort={activitySortBy}
                  currentOrder={activitySortOrder}
                  onSort={(f, o) => { setActivitySortBy(f); setActivitySortOrder(o); }}
                  className="px-6 py-4 w-[15%]"
                />
                <SortableHeader
                  label="Quantité"
                  field="quantity"
                  currentSort={activitySortBy}
                  currentOrder={activitySortOrder}
                  onSort={(f, o) => { setActivitySortBy(f); setActivitySortOrder(o); }}
                  className="px-6 py-4 w-[15%]"
                />
                <SortableHeader
                  label="Date"
                  field="createdAt"
                  currentSort={activitySortBy}
                  currentOrder={activitySortOrder}
                  onSort={(f, o) => { setActivitySortBy(f); setActivitySortOrder(o); }}
                  className="px-6 py-4 w-[20%]"
                />
                <th className="px-6 py-4 w-[15%] text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {stats.recentActivities.length > 0 ? stats.recentActivities.map(act => (
                <ActivityRow
                  key={act.id}
                  icon="inventory"
                  name={act.product?.name || 'Inconnu'}
                  type={act.type}
                  qty={act.quantity > 0 ? `+${act.quantity}` : act.quantity}
                  date={new Date(act.createdAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  status="Complété"
                  user={act.user}
                  poReference={getPoReference(act.description)}
                />
              )) : (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-400 italic">Aucune activité récente</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Add Modal (Placeholder) */}
      {showNewItemModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
              <h2 className="text-xl font-bold">Nouvel Article</h2>
              <button
                onClick={() => setShowNewItemModal(false)}
                className="size-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <span className="material-symbols-outlined text-4xl text-primary animate-bounce">add_box</span>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-2">Ajout rapide bientôt disponible</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Nous finalisons le module de gestion des articles. En attendant, vous pouvez naviguer dans les sites pour voir la répartition actuelle.
                </p>
              </div>
              <button
                onClick={() => setShowNewItemModal(false)}
                className="w-full py-3 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all"
              >
                D'accord, j'ai compris
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Sub-components
const KPICard = ({ title, value, trend, trendType, icon, isWarning, path }) => (
  <Link
    to={path}
    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl flex flex-col gap-2 hover:border-primary/30 hover:shadow-md transition-all group"
  >
    <div className="flex items-center justify-between">
      <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">{title}</span>
      <div className={`${isWarning ? 'bg-amber-500/10' : 'bg-primary/10'} p-2 rounded-lg group-hover:bg-primary group-hover:text-white transition-colors`}>
        <span className={`material-symbols-outlined ${isWarning ? 'text-amber-500 group-hover:text-white' : 'text-primary group-hover:text-white'}`}>{icon}</span>
      </div>
    </div>
    <p className="text-3xl font-bold dark:text-white">{value}</p>
    <div className={`flex items-center gap-1 text-sm font-bold ${trendType === 'up' ? 'text-emerald-500' : trendType === 'down' ? 'text-rose-500' : 'text-slate-400'
      }`}>
      {trendType !== 'neutral' && <span className="material-symbols-outlined text-sm">{trendType === 'up' ? 'trending_up' : 'trending_down'}</span>}
      <span>{trend}</span>
      {trendType !== 'neutral' && <span className="text-slate-400 font-normal ml-1">vs mois dernier</span>}
    </div>
  </Link>
);

const Bar = ({ height, label, active }) => (
  <div
    className={`flex-1 ${active ? 'bg-primary' : 'bg-primary/20 group-hover:bg-primary/40'} rounded-t-sm transition-all duration-500 ease-out min-h-[4px]`}
    style={{ height: height === '0%' ? '4px' : height }}
    title={label}
  ></div>
);

const AlertItem = ({ icon, name, stock, type }) => (
  <div className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 dark:border-slate-800 hover:border-primary/20 transition-colors group cursor-pointer">
    <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
      <span className="material-symbols-outlined text-slate-400 group-hover:text-primary transition-colors">{icon}</span>
    </div>
    <div className="flex-1">
      <h4 className="text-sm font-bold dark:text-white truncate group-hover:text-primary transition-colors">{name}</h4>
      <p className={`text-xs font-bold ${type === 'critical' ? 'text-rose-500' : 'text-amber-500'}`}>{stock}</p>
    </div>
    <span className="material-symbols-outlined text-primary text-sm opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">arrow_forward</span>
  </div>
);

const getTypeColor = (type) => {
  if (type.includes('Entrée')) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (type.includes('Sortie')) return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400';
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
};

const getTypeIcon = (type) => {
  if (type.includes('Entrée')) return 'south_west';
  if (type.includes('Sortie')) return 'north_east';
  return 'tune';
};

const ActivityRow = ({ icon, name, type, qty, date, status, user, statusColor = 'emerald', poReference }) => (
  <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-default">
    <td className="px-6 py-4">
      <div className="flex items-center gap-3">
        {user ? (
          user.avatar ? (
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700">
              <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black border border-primary/20 shrink-0">
              {user.name ? user.name.charAt(0).toUpperCase() : '?'}
            </div>
          )
        ) : (
          <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-sm text-slate-500">{icon}</span>
          </div>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-semibold dark:text-white leading-tight">{name}</span>
          {user && <span className="text-slate-400 text-[10px] leading-tight font-medium mt-0.5">Par {user.name}</span>}
        </div>
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="flex flex-col gap-1 items-start">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-tight ${getTypeColor(type)}`}>
          <span className="material-symbols-outlined text-[12px] mr-1 font-bold">{getTypeIcon(type)}</span>
          {type}
        </span>
        {poReference && (
          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 w-fit">
            Ref: {poReference}
          </span>
        )}
      </div>
    </td>
    <td className={`px-6 py-4 text-sm font-bold ${String(qty).startsWith('+') ? 'text-emerald-500' : String(qty).startsWith('-') ? 'text-rose-500' : 'text-slate-500'}`}>{qty}</td>
    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">{date}</td>
    <td className="px-6 py-4">
      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${statusColor === 'emerald'
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-500'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-500'
        }`}>
        {status}
      </span>
    </td>
  </tr>
);

const PredictionCard = ({ product }) => {
  const days = product.autonomy;
  const isCritical = days !== null && days <= 3;
  const isWarning = days !== null && days <= 7;

  const bgColor = isCritical ? 'bg-rose-500/10 border-rose-200 dark:border-rose-900/30' :
    isWarning ? 'bg-amber-500/10 border-amber-200 dark:border-amber-900/30' :
      'bg-emerald-500/10 border-emerald-200 dark:border-emerald-900/30';

  const textColor = isCritical ? 'text-rose-600 dark:text-rose-400' :
    isWarning ? 'text-amber-600 dark:text-amber-400' :
      'text-emerald-600 dark:text-emerald-400';

  return (
    <div className={`p-4 rounded-2xl border transition-all hover:shadow-md ${bgColor}`}>
      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-black text-slate-800 dark:text-white uppercase truncate" title={product.name}>
          {product.name}
        </h4>
        <div className="flex items-end justify-between gap-2">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase">Stock</span>
            <span className="text-xl font-black text-slate-900 dark:text-white">{product.quantity}</span>
          </div>
          <div className="flex flex-col items-end text-right">
            <span className="text-[10px] font-bold text-slate-400 uppercase">Rupture dans</span>
            <span className={`text-xl font-black ${textColor}`}>
              {days === null ? '∞' : days === 0 ? 'MAINTENANT' : `${days}j`}
            </span>
          </div>
        </div>
        <div className="w-full bg-black/5 dark:bg-white/5 h-1.5 rounded-full mt-2 overflow-hidden">
          <div
            className={`h-full rounded-full ${isCritical ? 'bg-rose-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: days === null ? '100%' : `${Math.min(100, (days / 15) * 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
